<template>
  <v-expansion-panel>
    <v-expansion-panel-header class="pa-0">
      <v-row align="center" style="order: 1">
        <v-col :cols="count === 1 || $vuetify.breakpoint.xs ? 12 : $vuetify.breakpoint.sm ? 4 : 6">
          <p class="heading mb-0">
            BEWERTUNGSKENNZAHL <span class="ml-1">{{
              rateParamMappings.find(rpo => rpo.key === valueObj.financialDataItemId).text
            }}</span>
          </p>
        </v-col>
        <v-col v-if="count > 1 && $vuetify.breakpoint.smAndUp"
               :cols="$vuetify.breakpoint.sm ? 8 : 6"
               align="right">
          <span class="mr-4">{{ valueObj.result | finCurrency }}</span>
          <v-icon v-if="valueObj.pricePotential > 0"
                  :color="$finColors.accentGreen"
                  class="mr-2">
            icon-value-up
          </v-icon>
          <v-icon v-if="valueObj.pricePotential < 0"
                  :color="$finColors.accentRed"
                  class="mr-2">
            icon-value-down
          </v-icon>
          <span class="mr-4">{{ valueObj.pricePotential | percent }}</span>
          <span>|</span>
          <span class="mx-4 weight-label">Gewichtung</span>
          <v-text-field v-model.number="valueObj.weight"
                        append-icon="mdi-percent-outline"
                        class="weight"
                        dense
                        outlined
                        type="number"/>
        </v-col>
      </v-row>
    </v-expansion-panel-header>


    <v-expansion-panel-content>
      <v-row class="row-separator ma-0" style="background-color: #fff;">
        <v-col v-if="$vuetify.breakpoint.xs"
               cols="12">
          <v-row no-gutters>
            <v-col cols="6">
              {{ valueObj.result | finCurrency }}
            </v-col>
            <v-col align="right">
              <v-icon v-if="valueObj.pricePotential > 0"
                      :color="$finColors.accentGreen"
                      class="mr-2">
                icon-value-up
              </v-icon>
              <v-icon v-if="valueObj.pricePotential < 0"
                      :color="$finColors.accentRed"
                      class="mr-2">
                icon-value-down
              </v-icon>
              <span>{{ valueObj.pricePotential | percent }}</span>
            </v-col>
          </v-row>
          <v-row no-gutters>
            <v-col align-self="center" cols="6">
              Gewichtung
            </v-col>
            <v-col align="right" cols="6">
              <v-text-field v-model.number="valueObj.weight"
                            append-icon="mdi-percent-outline"
                            class="weight"
                            dense
                            hide-details
                            type="number"/>
            </v-col>
          </v-row>
        </v-col>

        <v-col cols="12" sm="3" id="comparison-group-col">
          <p class="col-label">Vergleichsgruppe</p>
          <span v-if="sector">{{ sector.name }}</span>
          <v-btn v-if="!hideCustomCompareGroup && valueObj.initial" icon small @click="$emit('changeCompareGroup')">
            <v-icon>icon-edit</v-icon>
          </v-btn>
        </v-col>
        <v-col cols="12" sm="3" id="evaluation-parameter-col">
          <p class="col-label">Bewertungsparameter</p>
          <v-select v-model="valueObj.financialDataItemId"
                    :items="rateParamMappings"
                    dense
                    hide-details
                    item-text="text"
                    item-value="key"
                    outlined
                    @change="$emit('classificationChanged')"/>
        </v-col>
        <v-col cols="12" sm="3" id="base-col">
          <p class="col-label">Basis</p>
          <v-select v-model="valueObj.basis"
                    :items="basisOptions"
                    dense
                    hide-details
                    item-text="text"
                    item-value="key"
                    outlined
                    @change="$emit('classificationChanged')"/>
        </v-col>
        <v-col cols="12" sm="3">
          <p class="col-label">Ergebnis</p>
          <span class="result">{{ sectorResult | numberFormatted }}</span>
        </v-col>
      </v-row>

      <v-row class="ma-0" style="background-color: #fff;">
        <v-col cols="12" sm="3">
          <p class="col-label">Deine Aktie</p>
          {{ share.name }}
        </v-col>
        <v-col cols="12" sm="3">
          <p class="col-label">Bewertungsparameter</p>
          <fin-lexicon-text :text="rateParamMappings.find(rpo => rpo.key === valueObj.financialDataItemId).label"
                            auto-solve/>
        </v-col>
        <v-col cols="6" sm="3">
          <p class="col-label">Basis</p>
          {{ basisOptions.find(bo => bo.key === valueObj.basis).text }}
        </v-col>
        <v-col id="result-col" :style="{ border: result < 0 ? '2px solid ' + $finColors.accentRed : ''}" cols="6"
               sm="3">
          <p class="col-label">Ergebnis</p>
          <span v-if="!manualChangeResult" class="result">{{ result() | finCurrency }}</span>
          <v-btn v-if="!manualChangeResult" icon small @click="editResult">
            <v-icon>icon-edit</v-icon>
          </v-btn>

          <fin-currency-field v-if="manualChangeResult"
                              v-model="valueObj.customSharePositionValue"
                              class="mr-2"
                              dense
                              style="display: inline-block;"
                              width="150px"/>
          <v-btn v-if="manualChangeResult" icon small title="Wert wiederherstellen" @click="closeCustomResult">
            <v-icon small>icon-refresh</v-icon>
          </v-btn>
          <v-btn v-if="manualChangeResult" icon small title="Geänderten Wert übernehmen"
                 @click="changeResult">
            <v-icon small>icon-save</v-icon>
          </v-btn>
        </v-col>
      </v-row>

      <v-row v-if="result() < 0" style="background-color: #fff;">
        <v-col>
          <fin-error-display :error-msg="getNegativeValuationErrorMsg()"/>
          <fin-lexicon-link to="grenzen-der-multiplikatormethode">Mehr hierzu findest du im finLexikon
          </fin-lexicon-link>
        </v-col>
      </v-row>

      <v-row v-if="!valueObj.initial" no-gutters>
        <v-col>
          <v-btn class="pt-4"
                 icon
                 title="Kennzahl löschen"
                 @click="deleteClassificationNumberBlock"
          >
            <v-icon>icon-delete</v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </v-expansion-panel-content>
  </v-expansion-panel>
</template>

<script>
import FinLexiconText from '@/components/common/lexicon/LexiconText'
import FinCurrencyField from '@/components/common/CurrencyField'
import FinErrorDisplay from "@/components/common/ErrorDisplay"
import FinLexiconLink from "@/components/common/lexicon/LexiconLink"

export default {
  name: "finClassificationNumberBlock",
  components: {
    FinLexiconLink,
    FinErrorDisplay,
    FinCurrencyField,
    FinLexiconText
  },
  props: {
    /** The share rating condition object */
    value: {
      type: Object
    },
    /** The nr of the share rating condition */
    id: {
      type: Number
    },
    /** The mappings for the rating param select */
    rateParamMappings: {
      type: Array,
      default: () => []
    },
    /** The total nr of rating conditions */
    count: {
      type: Number,
      default: 0
    },
    /** The sector of the share to rate */
    sector: {
      type: Object
    },
    /** The share which is getting rated */
    share: {
      type: Object
    },
  },
  data() {
    return {
      valueObj: this.value,
      rateParamVal: this.ratingParam,
      basisOptions: [{
        text: 'laufendes Geschäftsjahr',
        key: 'CURRENT_YEAR'
      }, {
        text: 'nächstes Geschäftsjahr',
        key: 'NEXT_YEAR'
      }, {
        text: '5-Jahresdurchschnitt',
        key: 'LAST_5YEARS'
      }],
      basisVal: this.basis,
      weightVal: this.weight,
      manualChangeResult: false,
      hideCustomCompareGroup: process.env.VUE_APP_TOOLS_BDA_HIDE_CUSTOMGROUP === 'true',
    }
  },
  computed: {
    sectorResult() {
      if (this.sector && this.sector.ratios && this.sector.ratios.items) {
        return this.sector.ratios.items.find(r => r.basis === this.valueObj.basis && r.position === this.valueObj.financialDataItemId).value
      } else {
        return 0
      }
    },
  },
  methods: {
    getNegativeValuationErrorMsg() {
      return 'Das Bewertungskriterium ' + this.rateParamMappings.find(rpo => rpo.key === this.value.financialDataItemId).text +
        ' für das gewählte Wertpapier ist negativ, deshalb läßt sich kein sinnvoller fairer Wert berechnen. ' +
        'Wähle eine andere Kennzahl oder einen anderen Bewertungszeitraum.'
    },
    deleteClassificationNumberBlock() {
      this.$confirm({
        title: 'Kennzahl löschen',
        text: 'Möchtest du die aktuelle Kennzahl wirklich löschen?',
        confirmBtn: 'Löschen',
        textWithLexikonLink: false,
        cancelBtn: 'Abbrechen'
      }).then(() => {
        this.$emit('deleteConfig', this.id)
      })
    },
    result() {
      if (this.valueObj.customSharePositionValue) {
        return this.valueObj.customSharePositionValue
      } else if (this.share) {
        const shareAttr = this.rateParamMappings.find(om => om.key === this.valueObj.financialDataItemId).mappedKey
        return this.share[shareAttr]
      } else {
        return 0
      }
    },
    editResult() {
      this.valueObj.customSharePositionValue = this.result()
      this.manualChangeResult = true
    },
    changeResult() {
      this.manualChangeResult = false
      this.$emit('classificationChanged')
    },
    closeCustomResult() {
      this.manualChangeResult = false
      // reset
      this.valueObj.customSharePositionValue = null
      this.$emit('classificationChanged')
    }
  },
  watch: {
    value(val) {
      this.valueObj = val
    }
  }
}
</script>

<style lang="scss" scoped>
.heading {
  font-family: $body-font-family;
  font-size: 12px;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.17;
  letter-spacing: 1px;
  color: $fin-color-primary-dark;
}

.col-label {
  font-family: $body-font-family;
  font-size: 12px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  color: $fin-color-primary-dark;
  /* additional */
  margin-bottom: 4px;
}

.weight-label {
  font-family: $body-font-family;
  font-size: 14px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  color: $fin-color-primary-dark;
}

.weight {
  display: inline-block;
  max-width: 80px;
}

.percentage ::v-deep .v-input__icon {
  width: 16px;
  min-width: 16px;
  height: 16px;
}

.row-separator {
  border-bottom: solid 1px $fin-color-light-sky-blue;
}

.result {
  font-family: $body-font-family;
  font-size: 16px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.38;
  letter-spacing: normal;
  color: $fin-color-primary-dark;
}

::v-deep .v-expansion-panel-header__icon {
  order: 0;
}

::v-deep .v-expansion-panel-content__wrap {
  padding: 0 0 16px;
}
</style>
